/**
 * Coinbase Derivatives — READ-ONLY Decision Lab page.
 * No trading controls, no start/stop, no mutation.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { CurrencyBitcoin, Refresh } from '@mui/icons-material';
import { getCoinbaseDecisionLab, type CoinbaseDecisionLabPayload } from '../services/coinbaseService';
import CoinbaseDecisionLabPanel from '../components/coinbase/CoinbaseDecisionLabPanel';

const REFRESH_MS = 5000;

export default function CoinbaseDerivativesPage() {
  const [payload, setPayload] = useState<CoinbaseDecisionLabPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const data = await getCoinbaseDecisionLab();
      setPayload(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to load Decision Lab');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => { void load(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CurrencyBitcoin sx={{ fontSize: 30, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {payload?.engine.mode === 'MICRO_LIVE' || payload?.engine.mode === 'INCOME_LIVE'
                ? 'Live Command Center'
                : 'Coinbase Derivatives'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {payload?.engine.mode === 'MICRO_LIVE' || payload?.engine.mode === 'INCOME_LIVE'
                ? `${payload.engine.phase} · CFM Nano Futures · Real orders enabled`
                : 'Decision Lab · Read-only'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {payload && (() => {
            const isLive = payload.engine.mode === 'MICRO_LIVE' || payload.engine.mode === 'INCOME_LIVE';
            return (
              <Chip
                size="small"
                label={isLive ? `LIVE · ${payload.engine.mode}` : payload.engine.mode}
                color={isLive ? 'success' : payload.engine.status === 'DEGRADED' ? 'warning' : 'primary'}
                variant={isLive ? 'filled' : 'outlined'}
              />
            );
          })()}
          <Tooltip title="Refresh now">
            <span>
              <IconButton size="small" onClick={() => { void load(true); }} disabled={refreshing}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {loading && !payload && (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Connecting to engine…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {payload && <CoinbaseDecisionLabPanel payload={payload} />}
    </Box>
  );
}
